/**
 * 서버 스냅샷(/dashboard/pipeline/snapshot 응답) → 화면 스냅샷(PipelineSnapshot) 조립.
 *
 * 서버가 실제로 관측하는 것은 "샷 행이 equipment_shot_history 에 도착했다"는 사실과
 * ETL 동기화 기록, 알람이다. 그래서
 * - 설비 노드, Uploader(적재), 저장 테이블, 알람 이벤트는 실측값으로 채우고
 * - 수집기·Outbox·원시 테이블은 수집기가 상태를 보고하지 않아 도착 통계로 **추정**한다.
 *   추정인 노드는 설명 문구와 extra["데이터 출처"] 에 그렇게 적는다.
 * - 측정하지 않는 지표(성공률·재시도, 데이터가 없는 노드의 처리량·지연)는 null 로 두어
 *   화면이 '—' 로 그리게 한다. 0 이나 100 같은 그럴듯한 숫자를 만들어 넣지 않는다.
 * 노드 상태값은 types/pipeline.js 의 ALL_NODE_STATUSES 안에서만 고른다.
 */
import { applyTopology, FACTORIES, MACHINES } from "../data/machines";
import { nodeId, OUTPUT_DASHBOARD, OUTPUT_MONITOR, TABLE_NAMES } from "./node-ids";
import { computeSummary, createBaseNodes, createEdges, deriveEdges, round1 } from "./pipeline-graph";

/** 대기 판정 창(초). 백엔드 EquipmentLiveStatus 의 IDLE 창(30분)과 같은 값. 가동 판정 자체는 서버 liveStatus 를 쓴다. */
const IDLE_SEC = 30 * 60;
/** ETL 동기화가 이 시간보다 오래 멈추면 경고 */
const ETL_STALE_SEC = 60 * 60;
const INFERRED = "추정 — 수집기가 상태를 보고하지 않아 설비 샷 도착 통계로 판단";
const NOT_MEASURED = "미측정";

const toIso = (s) => {
    if (!s) return null;
    const ms = Date.parse(s);
    return Number.isNaN(ms) ? null : new Date(ms).toISOString();
};
const ageSec = (iso, nowMs) => {
    if (!iso) return null;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? null : Math.max(0, Math.round((nowMs - ms) / 1000));
};
const fmtAge = (sec) => {
    if (sec == null) return "기록 없음";
    if (sec < 60) return `${sec}s 전`;
    if (sec < 3600) return `${Math.floor(sec / 60)}분 전`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}시간 전`;
    return `${Math.floor(sec / 86400)}일 전`;
};
const fmtTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/** 도착 통계만으로 흐름 상태를 정한다: 10분 내 도착 → running, 30분 내 → warning, 그 외 offline */
function flowStatus(rows10m, lastAgeSec) {
    if (rows10m > 0) return "running";
    if (lastAgeSec != null && lastAgeSec <= IDLE_SEC) return "warning";
    return "offline";
}

export function buildLiveSnapshot(api) {
    const nowIso = toIso(api?.generatedAt) ?? new Date().toISOString();
    const nowMs = Date.parse(nowIso);
    applyTopology(api?.factories, api?.machines);

    const nodes = createBaseNodes(nowIso);
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const events = [];

    // ---- 설비 (실측) ----
    const runningByFactory = new Map();
    for (const m of api?.machines ?? []) {
        const n = byId.get(nodeId.machine(m.slug, m.machineId));
        if (!n) continue;
        const receivedIso = toIso(m.lastReceivedAt);
        const age = ageSec(receivedIso, nowMs);
        const shots10m = Number(m.shots10m) || 0;
        let status;
        let description;
        if (m.liveStatus === "RUNNING") {
            status = "running";
            description = `샷 수집 중 — 최근 10분 ${shots10m}샷, 마지막 도착 ${fmtAge(age)}`;
            runningByFactory.set(m.slug, (runningByFactory.get(m.slug) ?? 0) + 1);
        } else if (m.liveStatus === "IDLE") {
            status = "warning";
            description = `10분 이상 샷 없음 — 대기 또는 수집 지연 (마지막 도착 ${fmtAge(age)})`;
        } else {
            status = "offline";
            description = receivedIso
                ? `30분 이상 샷 없음 — 정지 또는 수집 중단 (마지막 도착 ${fmtAge(age)})`
                : "최근 7일 샷 기록 없음";
        }
        n.data = {
            ...n.data,
            label: m.equipmentName || n.data.label,
            status,
            description,
            throughput: round1(shots10m / 10),
            latency: age,                 // 도착 기록이 없으면 null → '—'
            successRate: null,            // 판정 컬럼이 비어 있어 양품 비율을 낼 수 없다
            lastUpdated: receivedIso ?? nowIso,
            // 공정/비전 정합률(24h) = 공정 샷 중 UUID 로 비전 기록이 매칭된 비율. 비전 기록이 없는 설비는 null(미연동)
            consistencyRate: m.consistencyRate != null ? Number(m.consistencyRate) : null,
            consistencySamples: Number(m.consistencySamples) || 0,
            extra: {
                "설비 코드": m.equipmentCode ?? "-",
                "가동 판정": m.liveStatus ?? "-",
                "마지막 샷(설비 시각)": fmtTime(toIso(m.lastShotAt)),
                "서버 도착": fmtTime(receivedIso),
                "최근 1분 샷": Number(m.shots1m) || 0,
                "최근 10분 샷": shots10m,
                "마지막 shot_count": m.lastShotCount ?? "-",
                "cycle_time": m.lastCycleTime ?? "-",
                "quality": m.lastQuality ?? NOT_MEASURED,
                "정합률(공정/비전)": m.consistencyRate != null
                    ? `${Number(m.consistencyRate)}% (24h 공정 샷 ${Number(m.consistencySamples) || 0}건 중 UUID 매칭)`
                    : "미연동",
            },
        };
    }

    // ---- 공장별 처리기 (수집기·Outbox 는 추정, Uploader 는 도착 실측) ----
    for (const f of api?.factories ?? []) {
        const collector = byId.get(nodeId.collector(f.slug));
        const outbox = byId.get(nodeId.outbox(f.slug));
        const uploader = byId.get(nodeId.uploader(f.slug));
        const legacy = byId.get(nodeId.legacy(f.slug));
        if (!collector || !outbox || !uploader || !legacy) continue;
        const rows1m = Number(f.rows1m) || 0;
        const rows10m = Number(f.rows10m) || 0;
        const lastIso = toIso(f.lastReceivedAt);
        const lastAge = ageSec(lastIso, nowMs);
        const running = runningByFactory.get(f.slug) ?? 0;
        const total = f.machineCount ?? 0;
        const perMin = round1(rows10m / 10);
        const flow = flowStatus(rows10m, lastAge);
        const flowText = rows10m > 0
            ? `최근 10분 ${rows10m}행 도착, 가동 설비 ${running}/${total}`
            : `최근 10분 도착 없음 (마지막 ${fmtAge(lastAge)})`;

        collector.data = {
            ...collector.data,
            status: flow,
            description: `${INFERRED}. ${flowText}`,
            throughput: perMin,           // 이 단계를 통과해 도착한 행 수(하한). 수집기 자체 계수는 아님
            latency: lastAge,
            successRate: total > 0 ? round1((running / total) * 100) : null,   // 가동 설비 비율(실측 판정 기반)
            lastUpdated: lastIso ?? nowIso,
            extra: { "데이터 출처": INFERRED, "가동 설비": `${running}/${total}`, "최근 10분 도착": rows10m, "마지막 도착": fmtTime(lastIso), "heartbeat": NOT_MEASURED, "큐 깊이": NOT_MEASURED },
        };
        outbox.data = {
            ...outbox.data,
            status: flow,
            description: `대기 건수 미측정 — ${INFERRED}`,
            throughput: perMin,
            latency: lastAge,
            successRate: null,
            lastUpdated: lastIso ?? nowIso,
            extra: { "데이터 출처": INFERRED, "outbox_pending": NOT_MEASURED },
        };
        uploader.data = {
            ...uploader.data,
            status: rows1m > 0 ? "running" : rows10m > 0 ? "healthy" : flow,
            description: `equipment_shot_history 도착 기준 — 최근 1분 ${rows1m}행 · 10분 ${rows10m}행`,
            throughput: rows1m,
            latency: lastAge,
            successRate: null,            // 업로드 실패 건수를 알 수 없다
            lastUpdated: lastIso ?? nowIso,
            extra: { "데이터 출처": "실측 (샷 행 created_at)", "uploaded_rows_1m": rows1m, "rows_10m": rows10m, "마지막 도착": fmtTime(lastIso), "upload_fail_1m": NOT_MEASURED },
        };
        legacy.data = {
            ...legacy.data,
            label: `raw ${legacy.data.label.replace(/^legacy\s*/, "")}`,
            description: `원시 수집 테이블 (${f.factoryName}, 설비별 1테이블) — ${INFERRED}`,
            status: flow,
            throughput: perMin,
            latency: lastAge,
            successRate: null,
            lastUpdated: lastIso ?? nowIso,
            extra: { "데이터 출처": INFERRED },
        };
    }

    // ---- 저장 계층 ----
    const st = api?.storage ?? {};
    const rows1m = Number(st.rows1m) || 0;
    const rows10m = Number(st.rows10m) || 0;
    const alarms10m = Number(st.alarms10m) || 0;
    const syncIso = toIso(st.lastSyncAt);
    const syncAge = ageSec(syncIso, nowMs);
    const syncOk = st.lastSyncStatus == null || st.lastSyncStatus === "SUCCESS";
    const set = (name, patch) => {
        const n = byId.get(nodeId.table(name));
        if (n) n.data = { ...n.data, lastUpdated: nowIso, successRate: null, ...patch };
    };
    set("shot_records", {
        label: "equipment_shot_history",
        description: "샷 단위 공정 데이터 (ETL 적재, 전 화면의 원천)",
        status: rows1m > 0 ? "running" : "healthy",
        throughput: round1(rows10m / 10),
        latency: null,
        extra: { "최근 1분 도착": rows1m, "최근 10분 도착": rows10m },
    });
    set("machine_status", {
        label: "설비 가동 판정",
        description: `마지막 샷 10분/30분 기준 실시간 판정 — 가동 ${st.running ?? 0} · 대기 ${st.idle ?? 0} · 정지 ${st.stopped ?? 0}`,
        status: "healthy",
        throughput: null,                 // 처리량 개념이 없는 노드
        latency: null,
        extra: { "가동": st.running ?? 0, "대기": st.idle ?? 0, "정지": st.stopped ?? 0 },
    });
    set("machine_events", {
        label: "equipment_alarms",
        description: `설비 알람 (ETL 적재) — 최근 10분 ${alarms10m}건`,
        status: alarms10m > 0 ? "running" : "healthy",
        throughput: round1(alarms10m / 10),
        latency: null,
        extra: { "최근 10분 알람": alarms10m },
    });
    set("collection_gaps", {
        label: "결번 구간 (미연동)",
        description: "SMI 에 결번 테이블이 없어 표시할 수 없음",
        status: "offline",
        throughput: null,
        latency: null,
        extra: { "데이터 출처": "없음" },
    });
    set("collector_heartbeat", {
        label: "etl_sync_status",
        description: syncIso
            ? `마지막 ETL 동기화 ${fmtTime(syncIso)} (${st.lastSyncStatus ?? "-"}${st.lastSyncRecords != null ? `, ${st.lastSyncRecords}건` : ""})`
            : "ETL 동기화 기록 없음",
        status: !syncIso ? "offline" : !syncOk ? "error" : syncAge > ETL_STALE_SEC ? "warning" : "healthy",
        throughput: null,
        latency: syncAge,                 // 마지막 동기화 후 경과
        lastUpdated: syncIso ?? nowIso,
        extra: { "sync_time": fmtTime(syncIso), "sync_status": st.lastSyncStatus ?? "-", "total_records": st.lastSyncRecords ?? "-" },
    });

    const dash = byId.get(OUTPUT_DASHBOARD);
    if (dash) dash.data = { ...dash.data, description: "이 화면 — 5초 주기 스냅샷 폴링", status: "running", throughput: null, latency: null, successRate: null, lastUpdated: nowIso };
    const mon = byId.get(OUTPUT_MONITOR);
    if (mon) mon.data = { ...mon.data, description: "현장 모니터 (미연동)", status: "offline", throughput: null, latency: null, successRate: null, lastUpdated: nowIso, extra: { "데이터 출처": "없음" } };

    // 재시도 횟수는 어느 단계에서도 측정하지 않는다 → 전 노드 '—'
    for (const n of nodes) n.data = { ...n.data, retryCount: null };

    // ---- 이벤트 ----
    for (const ev of api?.events ?? []) {
        const target = ev.slug && ev.machineId ? nodeId.machine(ev.slug, ev.machineId) : undefined;
        const name = target ? byId.get(target)?.data.label : null;
        events.push({
            id: ev.id,
            timestamp: toIso(ev.timestamp) ?? nowIso,
            level: ev.level ?? "info",
            nodeId: target,
            message: name ? `${name} — ${ev.message}` : ev.message,
        });
    }
    if (syncIso && !syncOk) {
        events.push({ id: `etl:${syncIso}`, timestamp: syncIso, level: "error", nodeId: nodeId.table("collector_heartbeat"), message: `ETL 동기화 실패 (${st.lastSyncStatus})` });
    }
    events.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));

    const edges = deriveEdges(nodes, createEdges());
    return {
        mode: "db",
        generatedAt: nowIso,
        nodes,
        edges,
        events,
        summary: computeSummary(nodes),
    };
}

/* 사이드바·상세 패널이 참조하는 상수도 같은 목록을 보도록 노출만 해 둔다 */
export { FACTORIES, MACHINES, TABLE_NAMES };
